import React, { useState, useEffect } from 'react';
import { Routes, Route, useParams, Navigate } from 'react-router-dom';
import AdminLogin from './components/AdminLogin';
import AdminDashboard from './components/AdminDashboard';
import ClientDashboard from './components/ClientDashboard';
import ClientService, { CLIENT_DATASET_MAP } from './services/clientService';
import { ClientData } from './types';
import { supabase } from './lib/supabase';
import AuthService from './services/authService';
import ScrollToTop from './components/ScrollToTop';
import BackToTop from './components/BackToTop';

async function hashPassword(value: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(value);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(hashBuffer))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

function ClientPage() {
  const { slug } = useParams<{ slug: string }>();
  const [clientData, setClientData] = useState<ClientData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [session, setSession] = useState(AuthService.getSession());
  const [isAdminMode, setIsAdminMode] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const isSessionAdmin = session?.user?.isAdmin ?? false;

  useEffect(() => {
    loadClientData();
  }, [slug]);

  const loadClientData = async () => {
    if (!slug) {
      setError('No client specified');
      setLoading(false);
      return;
    }

    try {
      const data = await ClientService.getClientBySlug(slug);
      setClientData(data || null);
    } catch (err) {
      console.error('Failed to load client data:', err);
      setClientData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!clientData?.id) return;

    const tablesToWatch = Array.from(
      new Set([
        ...Object.values(CLIENT_DATASET_MAP).map((config) => config.table),
        'clients',
      ])
    );

    const channel = supabase.channel(`realtime-client-${clientData.id}`);

    tablesToWatch.forEach((table) => {
      const filterColumn = table === 'clients' ? 'id' : 'client_id';
      channel.on(
        'postgres_changes',
        { event: '*', schema: 'public', table, filter: `${filterColumn}=eq.${clientData.id}` },
        () => {
          loadClientData();
        }
      );
    });

    channel.subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [clientData?.id, slug]);

  useEffect(() => {
    const handleStorage = () => {
      setSession(AuthService.getSession());
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  useEffect(() => {
    if (!clientData) return;

    const storageKey = clientData.slug ? `client_pass_${clientData.slug}` : '';

    if (isSessionAdmin) {
      setHasAccess(true);
      setPasswordError('');
      setPasswordInput('');
      return;
    }

    if (!clientData.pagePasswordHash) {
      if (storageKey) localStorage.removeItem(storageKey);
      setHasAccess(true);
      setPasswordInput('');
      setPasswordError('');
      return;
    }

    if (!storageKey) return;

    const saved = localStorage.getItem(storageKey);
    if (saved && saved === clientData.pagePasswordHash) {
      setHasAccess(true);
      setPasswordError('');
    } else {
      setHasAccess(false);
      setPasswordInput('');
      setPasswordError('');
    }
  }, [clientData?.pagePasswordHash, clientData?.slug, isSessionAdmin]);

  const handlePasswordSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!clientData) return;

    if (!clientData.pagePasswordHash) {
      setHasAccess(true);
      return;
    }

    const hashed = await hashPassword(passwordInput.trim());
    if (hashed === clientData.pagePasswordHash) {
      const storageKey = clientData.slug ? `client_pass_${clientData.slug}` : '';
      if (storageKey) localStorage.setItem(storageKey, hashed);
      setHasAccess(true);
      setPasswordError('');
      setPasswordInput('');
    } else {
      setPasswordError('Incorrect password.');
    }
  };

  const handleRequestAdmin = () => {
    if (isSessionAdmin) {
      setIsAdminMode(true);
    } else {
      window.location.href = 'https://data.littlefieldagency.com/la-admin';
    }
  };

  const handleExitAdmin = () => {
    AuthService.logout();
    setSession(null);
    setIsAdminMode(false);
    window.location.href = 'https://data.littlefieldagency.com/la-admin';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading Data...</p>
        </div>
      </div>
    );
  }

  if (error || !clientData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Client Not Found</h1>
          <p className="text-gray-600 mb-4">The requested client page could not be found.</p>
          <button
            onClick={() => (window.location.href = 'https://data.littlefieldagency.com/la-admin')}
            className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  if (isAdminMode && isSessionAdmin) {
    return <AdminDashboard onExitAdmin={handleExitAdmin} />;
  }

  if (!hasAccess) {
    return (
      <PasswordPrompt
        clientName={clientData.name}
        password={passwordInput}
        onChange={setPasswordInput}
        onSubmit={handlePasswordSubmit}
        error={passwordError}
      />
    );
  }

  return (
    <ClientDashboard
      clientData={clientData}
      onRequestAdmin={isSessionAdmin ? handleRequestAdmin : undefined}
    />
  );
}

function PasswordPrompt({
  clientName,
  password,
  onChange,
  onSubmit,
  error,
}: {
  clientName: string;
  password: string;
  onChange: (value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  error: string;
}) {
  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center px-4">
      <form
        onSubmit={onSubmit}
        className="bg-white rounded-xl shadow-2xl p-8 w-full max-w-md space-y-6"
      >
        <div className="text-center space-y-2">
          <h1 className="text-2xl font-bold text-slate-900">Protected Page</h1>
          <p className="text-sm text-slate-500">
            Enter the password to view insights for{' '}
            <span className="font-semibold">{clientName}</span>.
          </p>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => onChange(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
            placeholder="Enter password"
            required
          />
          {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
        </div>
        <button
          type="submit"
          className="w-full bg-teal-600 text-white py-2 rounded-md hover:bg-teal-700 transition-colors"
        >
          Unlock Page
        </button>
        <p className="text-xs text-slate-400 text-center">
          Need access? Contact your Littlefield Agency representative.
        </p>
      </form>
    </div>
  );
}

function AdminRoute() {
  const [session, setSession] = useState(AuthService.getSession());
  const isAuthenticated = !!session;

  const handleLogin = (success: boolean) => {
    if (success) {
      setSession(AuthService.getSession());
    }
  };

  const handleExit = () => {
    AuthService.logout();
    setSession(null);
    window.location.href = 'https://data.littlefieldagency.com/la-admin';
  };

  if (!isAuthenticated) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  return <AdminDashboard onExitAdmin={handleExit} />;
}

function App() {
  return (
    <>
      <ScrollToTop />
      <BackToTop />
      <Routes>
        <Route path="/la-admin" element={<AdminRoute />} />
        <Route path="/:slug/*" element={<ClientPage />} />
        <Route path="*" element={<Navigate to="/la-admin" replace />} />
      </Routes>
    </>
  );
}

export default App;
